import { useCurrentContentGraphQlClient, useCurrentSystemGraphQlClient, useTenantGraphQlClient } from "@contember/react-client";
import { useRef, useState, useCallback, useReducer, useEffect } from "react";
import { accessorTreeStateReducer } from "./accessorTreeStateReducer.js";
import { metadataToRequestError } from "./metadataToRequestError.js";
import { TreeStore } from "../core/TreeStore.js";
import { useIsMounted } from "@contember/react-utils";
import { useEnvironment } from "../accessorPropagation/useEnvironment.js";
import { DataBinding } from "../core/DataBinding.js";
import { SchemaLoader } from "../core/schema/SchemaLoader.js";
const useDataBinding = ({
  children,
  refreshOnPersist,
  skipStateUpdateAfterPersist
}) => {
  const contentClient = useCurrentContentGraphQlClient();
  const systemClient = useCurrentSystemGraphQlClient();
  const tenantClient = useTenantGraphQlClient();
  const environment = useEnvironment();
  const currentTreeStore = useRef();
  const isMountedRef = useIsMounted();
  const [schema, setSchema] = useState();
  const resetDataBinding = useCallback((environment2, newStore) => {
    const onUpdate = (data, binding2) => {
      if (isMountedRef.current) {
        dispatch({ type: "setData", data, binding: binding2 });
      }
    };
    const onError = (error, binding2) => {
      if (isMountedRef.current) {
        dispatch({ type: "failWithError", error, binding: binding2 });
      }
    };
    const onPersistSuccess = () => {
      if (isMountedRef.current && refreshOnPersist) {
        resetDataBinding(environment2, true);
      }
    };
    if (currentTreeStore.current === void 0 || newStore) {
      currentTreeStore.current = new TreeStore(environment2.getSchema());
    }
    const binding = new DataBinding(contentClient, systemClient, tenantClient, currentTreeStore.current, environment2, onUpdate, onError, onPersistSuccess, {
      skipStateUpdateAfterPersist: skipStateUpdateAfterPersist ?? false
    });
    dispatch({ type: "reset", binding, environment: environment2 });
  }, [contentClient, systemClient, tenantClient, isMountedRef, refreshOnPersist, skipStateUpdateAfterPersist]);
  const [state, dispatch] = useReducer(accessorTreeStateReducer, {
    name: "initializing",
    environment
  });
  useEffect(() => {
    if (schema !== void 0) {
      return;
    }
    (async () => {
      try {
        setSchema(await SchemaLoader.loadSchema(contentClient));
      } catch (metadata) {
        if (typeof metadata === "object" && metadata !== null && metadata.name === "AbortError") {
          return;
        }
        if (isMountedRef.current) {
          dispatch({
            type: "failWithError",
            error: metadataToRequestError(metadata),
            binding: state.binding
          });
        }
      }
    })();
  }, [contentClient, isMountedRef, schema, state.binding]);
  useEffect(() => {
    if (schema) {
      resetDataBinding(environment.withSchema(schema), false);
    }
  }, [resetDataBinding, schema, environment]);
  useEffect(() => {
    state.binding?.extendTree(children);
  }, [children, state.binding]);
  return state;
};
export {
  useDataBinding
};
//# sourceMappingURL=useDataBinding.js.map
